import L from 'leaflet';
import { onMounted, onBeforeUnmount, ref, type Ref } from 'vue';
import type { Coords, Segment } from '@/types';

/**
 * Composable Leaflet : initialise la carte et expose des helpers
 * pour ajouter / retirer marqueurs et lignes de manière déclarative.
 *
 * Volontairement minimaliste : ce composable ne sait rien d'EuroRail.
 * C'est le composant `MapArea.vue` qui consomme `useTrip()` et appelle
 * `addMarker` / `addLine` en fonction du state.
 *
 * On expose aussi `invalidateSize` (pour le drawer mobile) et `onMapClick`/`onMapEvent`
 * (pour brancher des effets côté composant sans fuite Leaflet).
 */

export interface UseMapOptions {
  elRef: Ref<HTMLElement | null>;
  initialCenter?: Coords;
  initialZoom?: number;
}

export interface MarkerOptions {
  iconHtml: string;
  iconSize: number;
  zIndexOffset?: number;
  tooltip?: string;
  /**
   * Contenu HTML du popup. Si fourni, ouvre un popup au clic.
   * Le composant parent doit attacher des listeners via `onPopupOpen`.
   */
  popupHtml?: string;
}

export function useMap({
  elRef,
  initialCenter = [48, 8],
  initialZoom = 4,
}: UseMapOptions) {
  const map = ref<L.Map | null>(null);
  const markers: L.Layer[] = [];
  const lines: L.Polyline[] = [];

  function isMobileNow(): boolean {
    return window.matchMedia('(max-width: 699px)').matches;
  }

  // ---------- Init / teardown ----------

  onMounted(() => {
    if (!elRef.value) return;

    const m = L.map(elRef.value, {
      zoomControl: !isMobileNow(),
      scrollWheelZoom: !isMobileNow(),
      touchZoom: true,
      // Sur mobile, on n'active PAS le drag par défaut : ça laisse la page scroller
      // librement. L'utilisateur active la carte explicitement (cf. activateMap).
      dragging: !isMobileNow(),
      minZoom: 3,
      maxZoom: 10,
    } as L.MapOptions).setView(initialCenter, initialZoom);

    // Tuiles claires en permanence (le dark mode rendait la carte peu lisible)
    L.tileLayer(
      'https://cartodb-basemaps-{s}.global.ssl.fastly.net/light_all/{z}/{x}/{y}.png',
      {
        attribution: '&copy; OpenStreetMap, &copy; CARTO',
        subdomains: 'abcd',
        maxZoom: 18,
      },
    ).addTo(m);

    map.value = m;
  });

  onBeforeUnmount(() => {
    map.value?.remove();
    map.value = null;
  });

  // ---------- Layer management ----------

  function clearLayers(): void {
    const m = map.value;
    if (!m) return;
    markers.forEach((l) => m.removeLayer(l));
    markers.length = 0;
    lines.forEach((l) => m.removeLayer(l));
    lines.length = 0;
  }

  function addMarker(
    coords: Coords,
    opts: MarkerOptions,
  ): L.Marker | null {
    const m = map.value;
    if (!m) return null;
    const size = opts.iconSize;
    const icon = L.divIcon({
      className: '',
      html: opts.iconHtml,
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
    });
    const marker = L.marker(coords, {
      icon,
      zIndexOffset: opts.zIndexOffset ?? 100,
    });
    if (opts.popupHtml) marker.bindPopup(opts.popupHtml);
    if (opts.tooltip) {
      marker.bindTooltip(opts.tooltip, {
        direction: 'top',
        offset: [0, opts.zIndexOffset && opts.zIndexOffset >= 500 ? -8 : -4],
      });
    }
    marker.addTo(m as L.Map);
    markers.push(marker);
    return marker;
  }

  /**
   * Trace une ligne avec un style adapté au type de segment.
   *
   * 4 styles distincts :
   *  - Train de nuit (≥ 6h) : violet, pointillés courts, plus opaque
   *  - Bus de nuit         : vert, pointillés courts (identique aux bus
   *                          mais pattern plus serré pour signaler "nuit")
   *  - Train classique     : bleu plein
   *  - Bus classique       : vert, tirets longs
   *
   * Le seuil 6h pour le train de nuit est aligné avec NIGHT_SEGMENT_MIN_DURATION
   * (cf. constants.ts). En deçà, on ne dort pas vraiment dans le train.
   */
  function addSegmentLine(from: Coords, to: Coords, seg: Segment): void {
    const m = map.value;
    if (!m) return;
    const trainCol = '#185fa5';
    const busCol = '#1d9e75';
    const nightCol = '#534ab7';

    const isNightTrain = seg.mode === 'train' && seg.night && seg.cost >= 360;
    const isNightBus = seg.mode === 'bus' && seg.nightBus;

    let color: string;
    let dashArray: string | undefined;

    if (isNightTrain) {
      color = nightCol;
      dashArray = '2,4';
    } else if (isNightBus) {
      color = busCol;
      dashArray = '2,4';
    } else if (seg.mode === 'bus') {
      color = busCol;
      dashArray = '8,5';
    } else {
      // train classique
      color = trainCol;
      dashArray = undefined;
    }

    const line = L.polyline([from, to], {
      color,
      weight: 3.5,
      opacity: 0.85,
      dashArray,
      lineCap: 'round',
    }).addTo(m as L.Map);
    lines.push(line);
  }

  function fitBounds(coords: Coords[]): void {
    const m = map.value;
    if (!m) return;
    if (coords.length >= 2) {
      m.fitBounds(coords, { padding: [60, 60], maxZoom: 6 });
    } else if (coords.length === 1) {
      m.setView(coords[0], 5);
    }
  }

  function setView(coords: Coords, zoom = 5): void {
    map.value?.setView(coords, zoom);
  }

  function invalidateSize(): void {
    map.value?.invalidateSize();
  }

  /** Bind un listener sur l'instance Leaflet (préserve le typage natif) */
  function on<T extends keyof L.LeafletEventHandlerFnMap>(
    event: T,
    handler: L.LeafletEventHandlerFnMap[T],
  ): void {
    map.value?.on(event, handler as any);
  }

  function closePopup(): void {
    map.value?.closePopup();
  }

  /**
   * Active les interactions tactiles (drag + zoom).
   * À appeler sur mobile quand l'utilisateur tape explicitement la carte
   * pour basculer en mode "exploration".
   */
  function activate(): void {
    const m = map.value;
    if (!m) return;
    m.dragging.enable();
    m.touchZoom.enable();
  }

  /**
   * Désactive le drag (la page peut scroller librement par-dessus).
   * Le tap zoom reste actif (zoom à 2 doigts = geste système).
   */
  function deactivate(): void {
    const m = map.value;
    if (!m) return;
    m.dragging.disable();
  }

  /** Renvoie `true` si le drag est actuellement actif */
  function isActive(): boolean {
    return map.value?.dragging.enabled() ?? false;
  }

  return {
    map,
    clearLayers,
    addMarker,
    addSegmentLine,
    fitBounds,
    setView,
    invalidateSize,
    on,
    closePopup,
    activate,
    deactivate,
    isActive,
  };
}
